import React, { useState, useRef, useEffect, useCallback } from "react";
import {
    XMarkIcon,
    MagnifyingGlassIcon,
    PencilIcon,
    CheckIcon,
    PlusIcon,
    CalendarIcon,
} from "@heroicons/react/24/outline";
import {
    Box,
    Drawer,
    IconButton,
    Paper,
    Typography,
    TextField,
    InputAdornment,
    Button,
} from "@mui/material";
import { DO_NOTHING } from "../../../utils/commonfunctions";
import ButtonComponent from "../../customButton";
import styles from "../../../styles/envandVariable/UpdateEnvironmentVariableSlider.module.scss";

const SLIDER_TITLE = "Environment Variable";

const headerCellStyle = {
    fontWeight: 600,
    color: "#64748b",
    fontSize: 13,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    lineHeight: 1.5,
};

function TableHeader() {
    return (
        <thead>
            <tr style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={{ width: "200px", padding: "12px 16px", textAlign: "left" }}>
                    <Typography sx={headerCellStyle}>Key</Typography>
                </th>
                <th style={{ padding: "12px 16px", textAlign: "left" }}>
                    <Typography sx={headerCellStyle}>Value</Typography>
                </th>
                <th style={{ width: "60px", padding: "12px 16px", textAlign: "center" }}>
                    <Typography sx={headerCellStyle}>Encrypted</Typography>
                </th>
            </tr>
        </thead>
    );
}

export default function UpdateEnvironmentVariableSlider({
    closePopup = DO_NOTHING,
    popupData = {},
    handleEnvironmentVariableUpdate = DO_NOTHING,
    loading = false,
}) {
    const [rows, setRows] = useState(popupData?.environmentVariables || []);
    const [editingCell, setEditingCell] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const inputRef = useRef(null);

    // Track updated variables: { [id]: updatedValue }
    const [updatedVariables, setUpdatedVariables] = useState({});

    const filteredRows = searchQuery
        ? rows.filter(row =>
            row.key.toLowerCase().includes(searchQuery.toLowerCase())
        )
        : rows;

    const handleCellDoubleClick = useCallback((rowId, field, value, encrypted) => {
        if (field === "value" || field === "key") {
            setEditingCell({ rowId, field, encrypted });
            setEditValue(value);
        }
    }, []);

    const handleEditChange = e => setEditValue(e.target.value);

    const handleEditSave = useCallback(() => {
        if (editingCell) {
            console.log(editingCell);
            setRows(prevRows =>
                prevRows.map(row =>
                    row.id === editingCell.rowId
                        ? { ...row, [editingCell.field]: editValue }
                        : row
                )
            );
            // Track updated value and its variable id
            if (editingCell.field === "value") {
                setUpdatedVariables(prev => ({
                    ...prev,
                    [editingCell.rowId]: {
                        value: editValue,
                        encrypted: editingCell.encrypted
                    },
                }));
            }
            setEditingCell(null);
            setEditValue("");
        }
    }, [editingCell, editValue]);

    const handleInputKeyDown = e => {
        if (e.key === "Enter") handleEditSave();
        else if (e.key === "Escape") {
            setEditingCell(null);
            setEditValue("");
        }
    };

    useEffect(() => {
        if (editingCell && inputRef.current) inputRef.current.focus();
        const handleClickOutside = event => {
            if (inputRef.current && !inputRef.current.contains(event.target)) {
                handleEditSave();
            }
        };
        if (editingCell) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [editingCell, handleEditSave]);

    // Table row rendering extracted for clarity and DRYness
    const renderRow = row => {
        const isEditingValue = editingCell?.rowId === row.id && editingCell?.field === "value";
        return (
            <tr key={row.id}>
                {/* Key Field */}
                <td style={{ width: "200px", padding: "4px 16px", verticalAlign: "middle" }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#334155" }}>
                        {row.key}
                    </Typography>
                </td>
                {/* Value Field */}
                <td style={{ padding: "4px 16px", verticalAlign: "middle" }}>
                    <Box
                        sx={{
                            cursor: isEditingValue ? "text" : "pointer",
                            minHeight: "28px",
                            height: "28px",
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            border: "1px solid #e2e8f0",
                            borderRadius: 2,
                            backgroundColor: isEditingValue ? "#fff" : "#f8fafc",
                            transition: "background-color 0.2s, border-color 0.2s",
                            "&:hover": {
                                backgroundColor: isEditingValue ? "#fff" : "#f1f5f9",
                                borderColor: "#cbd5e1",
                            },
                        }}
                        onClick={() => {
                            if (!isEditingValue)
                                handleCellDoubleClick(row.id, "value", row.value, row.encrypted);
                        }}
                    >
                        {isEditingValue ? (
                            <TextField
                                inputRef={inputRef}
                                value={editValue}
                                onChange={handleEditChange}
                                onBlur={handleEditSave}
                                onKeyDown={handleInputKeyDown}
                                fullWidth
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        fontSize: 13,
                                        backgroundColor: "#fff",
                                        borderRadius: 2,
                                        minHeight: "28px",
                                        height: "28px",
                                        padding: 0,
                                        border: "none",
                                    },
                                    "& .MuiInputBase-input": {
                                        padding: "4px 8px",
                                        height: "20px",
                                        textAlign: "left",
                                    },
                                }}
                                inputProps={{
                                    style: {
                                        minHeight: "28px",
                                        height: "28px",
                                        boxSizing: "border-box",
                                        padding: "4px 8px",
                                    },
                                }}
                            />
                        ) : (
                            <>
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        color: row.value ? "#334155" : "#94a3b8",
                                        fontStyle: row.value ? "normal" : "italic",
                                        wordBreak: "break-all",
                                        textAlign: "left",
                                        width: "100%",
                                        padding: "0 8px",
                                    }}
                                >
                                    {row.value || "Click to add value..."}
                                </Typography>
                                <IconButton
                                    onClick={() => handleCellDoubleClick(row.id, "value", row.value, row.encrypted)}
                                    sx={{
                                        color: "#64748b",
                                        "&:hover": { color: "#16a34a" },
                                    }}
                                >
                                    <PencilIcon style={{ width: 16, height: 16 }} />
                                </IconButton>
                            </>
                        )}
                    </Box>
                </td>
                {/* Encrypted */}
                <td
                    style={{
                        width: "60px",
                        padding: "4px 16px",
                        verticalAlign: "middle",
                        textAlign: "center",
                    }}
                >
                    <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#334155" }}>
                        {row.encrypted ? "Yes" : "No"}
                    </Typography>
                </td>
            </tr>
        );
    };

    // For demonstration, you can log updatedVariables or pass it to the update handler
    // console.log("Updated Variables:", updatedVariables);

    return (
        <Drawer
            open={true}
            onClose={closePopup}
            anchor="right"
            BackdropProps={{
                onClick: (e) => e.stopPropagation(),
                sx: {
                    backgroundColor: 'rgba(0, 0, 0, 0.25)',
                },
            }}
            className={styles.sliderContainer}
        >
            <Paper className={styles.innerContainer}>
                {/* Header */}
                <Box className={styles.headerContainer}>
                    <Box className={`${styles.iconCover} ${styles.green}`}>
                        <PlusIcon className={`icon ${styles.white}`} />
                    </Box>
                    <Box className={styles.contentContainer}>
                        <Typography className={styles.title}>
                            {SLIDER_TITLE}
                        </Typography>
                        <Box className={styles.subTitleContainer}>
                            <Typography className={styles.subtitle}>
                                {popupData?.environmentName}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        className={styles.closeIcon}
                        onClick={closePopup}
                        title="Close"
                    >
                        <XMarkIcon className={styles.icon} />
                    </IconButton>
                </Box>

                {/* Body */}
                <Box className={styles.bodyContainer}>
                    {/* Search Field */}
                    <Box sx={{ mb: 3 }}>
                        <TextField
                            placeholder="Search variables..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            fullWidth
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <MagnifyingGlassIcon
                                            style={{ width: 20, height: 20, color: "#64748b" }}
                                        />
                                    </InputAdornment>
                                ),
                                sx: {
                                    backgroundColor: "#f8fafc",
                                    borderRadius: 2,
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        border: "1px solid #e2e8f0",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#cbd5e1",
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#3b82f6",
                                    },
                                },
                            }}
                        />
                    </Box>

                    {/* Table */}
                    <Box sx={{ overflow: "hidden" }}>
                        {/* Note */}
                        <Typography
                            sx={{
                                color: "#64748b",
                                fontSize: 13,
                                fontStyle: "italic",
                                textAlign: "right",
                            }}
                        >
                            To edit, click the value field. Press Enter or click outside to save your changes.
                        </Typography>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <TableHeader />
                            <tbody>
                                <tr>
                                    <td
                                        colSpan={3}
                                        style={{
                                            height: 12,
                                            background: "transparent",
                                            border: "none",
                                            padding: 0,
                                        }}
                                    />
                                </tr>
                                {filteredRows.map(renderRow)}
                            </tbody>
                        </table>
                        {/* No Results Message */}
                        {filteredRows.length === 0 && searchQuery && (
                            <Box sx={{ textAlign: "center", py: 4, color: "#64748b" }}>
                                <Typography variant="body2">
                                    No variables found matching "{searchQuery}"
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
                {/* Footer Actions */}
                <Box className={styles.footerContainer}>
                    <Box className={styles.footerLeft}>
                        <CalendarIcon className={`${styles.iconLg} ${styles.slate}`} />
                        <p>Updating environment variables</p>
                    </Box>
                    <Box className={styles.footerRight}>
                        <ButtonComponent
                            variant="outline"
                            onClick={closePopup}
                            className={styles.cancelButton}
                            ctaText="Cancel"
                        />
                        <ButtonComponent
                            variant="primary"
                            onClick={() => {
                                handleEnvironmentVariableUpdate(updatedVariables, popupData?.environmentId)
                            }}
                            disabled={Object.keys(updatedVariables).length === 0 || loading}
                            className={styles.saveButton}
                            ctaText="Save Changes"
                            startIcon={<CheckIcon className={`${styles.icon} ${styles.white}`} />}
                        />
                    </Box>
                </Box>
            </Paper>
        </Drawer>
    );
}
